import { get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIconBrand } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { filterMajorOTAs } from 'lumen/utils/parities-filter';

interface ParityTablesCellsTdPosBaserateSignature {
  Args: {
    cellData: unknown;
    column: unknown;
  };
}

export default class ParityTablesCellsTdPosBaserate extends Component<ParityTablesCellsTdPosBaserateSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare ota: Services['ota'];
  @service declare rates: Services['rates'];

  // value is the parities object from the model (loop over all the parities to find the base rate)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baseRateObject() {
    let baseRateObject = null;

    // @ts-expect-error: Incorrect type
    if (this.period.parities) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.period.parities.some((parityDataPerOta) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (parityDataPerOta.is_baserate) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          baseRateObject = parityDataPerOta;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        return parityDataPerOta.is_baserate;
      });
    }

    return baseRateObject;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baseRates() {
    const { cellData, column } = this.args;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    const otaIds = column.availableOTAs?.map((ota) => ota.id);

    const baseRates = filterMajorOTAs(
      otaIds,
      // @ts-expect-error: Incorrect type
      cellData.paritiesPOSDict,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      (parity) => parity.is_baserate,
    );

    return baseRates.map((baseRate) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { channel, currency, extract_datetime, ota } = baseRate;

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        channel,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        extractDatetimeFromNow: dayjs.utc(extract_datetime).fromNow(),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        extract_datetime,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        rate: this.rates.formatRate(baseRate, this.hotel),
        rateUnconverted: [
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          this.rates.formatRateObject(baseRate, false),
          currency,
        ].join(' '),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        source: this.ota.otaIdToPrettyName(ota),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        title: this.ota.otaIdToPrettyName(channel),
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get baseRateValue() {
    if (this.baseRateObject) {
      // @ts-expect-error: Incorrect type
      if (this.baseRateObject?.message) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return this.intl.t(this.baseRateObject?.message);
      }

      return `${this.rates.formatRateObject(this.baseRateObject, false)} ${
        // @ts-expect-error: Incorrect type
        this.baseRateObject?.currency
      }`;
    }

    return this.intl.t('general.not_available_short');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get day() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.period.day;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isToday() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.period.day.isToday;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get period() {
    return this.args.cellData;
  }

  // quick admin only way to check API responses in the console
  @action
  onDoubleClick(): void {
    if (this.access.isStaffUser && console) {
      console.group(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        `Debugging info: ${this.day?.date.format('ddd l')} - parities objects`,
      );
      Array.prototype.forEach.call(
        // @ts-expect-error: Incorrect type
        this.period.paritiesPOSDict,
        (parityDataPerOta) => {
          console.log(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            `-- ${parityDataPerOta.ota} - ${parityDataPerOta.channel} --`,
          );
          console.dir(parityDataPerOta);
        },
      );

      console.groupEnd();
    }
  }

  <template>
    <div
      class="flex items-center justify-end tabular-nums
        {{if this.isToday 'td-today'}}"
    >
      <span
        class="parity-meet"
        role="tooltip"
        {{on "dblclick" this.onDoubleClick}}
      >
        {{this.baseRateValue}}
        {{#if this.baseRateObject}}
          <EmberTooltip
            @delay={{25}}
            @side="bottom"
            @tooltipClass="base-tooltip table-pos-tooltip"
          >
            <table cellspacing="0">
              <thead>
                <tr>
                  <th>{{t "parity.channel"}}</th>
                  <th>{{t "parity.lowest_rate"}}</th>
                  <th>{{t "parity.source"}}</th>
                  <th>{{t "general.update"}}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div class="flex items-center gap-100">
                      {{#each this.baseRates as |baseRate|}}
                        <PrismIconBrand @name={{baseRate.channel}} />
                      {{/each}}
                    </div>
                  </td>
                  {{! @glint-expect-error: Incorrect type }}
                  <td>{{get this.baseRates "0.rateUnconverted"}}</td>
                  {{! @glint-expect-error: Incorrect type }}
                  <td>{{get this.baseRates "0.source"}}</td>
                  {{! @glint-expect-error: Incorrect type }}
                  <td>{{get this.baseRates "0.extractDatetimeFromNow"}}</td>
                </tr>
              </tbody>
            </table>
          </EmberTooltip>
        {{/if}}
      </span>
    </div>
  </template>
}
